/* Aesthesy search — Figma 34:5202 / 34:5306 / 34:5448.

   Drives BOTH panel instances (the header drawer and the one on /search):
     - opens/closes the drawer and pins it under the real header height
     - debounced predictive-search fetch into [data-aes-search-results]
     - bolds the matched run of each suggestion title
     - recent searches in localStorage, with the Clear button

   Deliberately not assets/predictive-search.js: that component reaches for a
   `.header` element and a fixed button set from the Be Yours header and throws
   against aes-header. Markup contract lives in snippets/aes-search-panel.liquid. */
(() => {
  if (window.__aesSearchBooted) return;
  window.__aesSearchBooted = true;

  const KEY = 'aes:recent-searches';
  const MAX_RECENT = 8;

  /* ---- recent searches (per browser; storage can throw in private mode) --- */
  const readRecent = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  };
  const writeRecent = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
    } catch (e) {
      /* storage unavailable — recent searches just don't persist */
    }
  };
  const rememberTerm = (term) => {
    const q = (term || '').trim();
    if (!q) return;
    const list = readRecent().filter((t) => t.toLowerCase() !== q.toLowerCase());
    list.unshift(q);
    writeRecent(list);
  };

  /* ---- highlight the matched run without ever using innerHTML ------------- */
  const markMatch = (el, term) => {
    const text = el.textContent;
    const i = text.toLowerCase().indexOf(term.toLowerCase());
    if (!term || i < 0) return;
    const match = document.createElement('span');
    match.className = 'aes-search__match';
    match.textContent = text.slice(i, i + term.length);
    el.textContent = '';
    el.append(document.createTextNode(text.slice(0, i)), match, document.createTextNode(text.slice(i + term.length)));
  };

  const build = (root) => {
    if (root.dataset.aesBound) return;
    root.dataset.aesBound = '1';

    const form = root.querySelector('form');
    const input = root.querySelector('[data-aes-search-input]');
    const results = root.querySelector('[data-aes-search-results]');
    const recentBlock = root.querySelector('[data-aes-search-recent]');
    const chips = root.querySelector('[data-aes-search-chips]');
    const clearBtn = root.querySelector('[data-aes-search-clear]');
    const scrim = root.querySelector('[data-aes-search-scrim]');
    const isDrawer = root.classList.contains('aes-search--drawer');
    if (!form || !input) return;

    const endpoint = root.dataset.url || '/search/suggest';
    const limit = root.dataset.limit || 6;

    /* ---- recent chips --------------------------------------------------- */
    const renderRecent = () => {
      if (!recentBlock || !chips) return;
      const list = readRecent();
      chips.textContent = '';
      list.forEach((term) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'aes-search__chip';
        const icon = document.createElement('img');
        icon.src = root.dataset.recentIcon || '';
        icon.alt = '';
        icon.width = 20;
        icon.height = 20;
        const label = document.createElement('span');
        label.textContent = term;
        chip.append(icon, label);
        chip.addEventListener('click', () => {
          input.value = term;
          form.requestSubmit ? form.requestSubmit() : form.submit();
        });
        chips.append(chip);
      });
      recentBlock.hidden = list.length === 0;
    };

    clearBtn?.addEventListener('click', () => {
      writeRecent([]);
      renderRecent();
    });

    /* ---- predictive suggestions ------------------------------------------ */
    let controller;
    let timer;
    const suggest = (term) => {
      if (!results) return;
      if (term.length < 2) {
        results.textContent = '';
        return;
      }
      controller?.abort();
      controller = new AbortController();
      const url =
        `${endpoint}?q=${encodeURIComponent(term)}` +
        `&${encodeURIComponent('resources[type]')}=product` +
        `&${encodeURIComponent('resources[limit]')}=${limit}` +
        `&section_id=aes-predictive`;

      fetch(url, { signal: controller.signal })
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const section = doc.querySelector('#shopify-section-aes-predictive');
          results.innerHTML = section ? section.innerHTML : '';
          results.querySelectorAll('[data-aes-search-title]').forEach((el) => markMatch(el, term));
          results.querySelectorAll('.aes-search__row').forEach((row) =>
            row.addEventListener('click', () => rememberTerm(term))
          );
        })
        .catch((e) => {
          if (e.name !== 'AbortError') results.textContent = '';
        });
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const term = input.value.trim();
      timer = setTimeout(() => suggest(term), 250);
    });

    form.addEventListener('submit', () => rememberTerm(input.value));

    /* ---- drawer open/close ------------------------------------------------ */
    if (isDrawer) {
      const header = document.querySelector('.aes-hdr');
      const setTop = () => {
        const h = header ? Math.round(header.getBoundingClientRect().bottom) : 64;
        root.style.setProperty('--aes-hdr-h', `${Math.max(h, 0)}px`);
      };

      root.addEventListener('transitionend', (e) => {
        if (e.propertyName === 'clip-path' && root.hasAttribute('data-open')) {
          root.classList.add('is-expanded');
        }
      });

      const close = () => {
        root.classList.remove('is-expanded');
        root.removeAttribute('data-open');
        root.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        // Results are cleared once the closing sweep is over, not at its
        // start — emptying them instantly made the panel jump mid-close.
        if (results) setTimeout(() => { if (!root.hasAttribute('data-open')) results.textContent = ''; }, 450);
      };
      const open = () => {
        setTop();
        renderRecent();
        root.setAttribute('data-open', '');
        root.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        // Focus synchronously, still inside the tap gesture: iOS only raises the
        // keyboard for a focus() call that happens in the user-gesture task, so
        // deferring this to rAF or a transitionend silently kills the keyboard.
        input.focus({ preventScroll: true });
      };

      document.querySelectorAll('[data-aes-search-open]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          root.hasAttribute('data-open') ? close() : open();
        })
      );
      root.querySelector('[data-aes-search-close]')?.addEventListener('click', close);
      scrim?.addEventListener('click', close);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && root.hasAttribute('data-open')) close();
      });
      window.addEventListener('resize', () => {
        if (root.hasAttribute('data-open')) setTop();
      });
      setTop();
    } else {
      renderRecent();
    }
  };

  const boot = (scope) => (scope || document).querySelectorAll('[data-aes-search]').forEach(build);
  boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
